import { create } from 'zustand';
import {
  getCategories,
  getCatalogCourses,
  getFeaturedCourses,
  getRecommendationRules,
  Category,
  CatalogCourse,
  FeaturedCourse,
  RecommendationRule,
} from '@/services/PagesServices/CatalogManagementService';

interface CatalogManagementState {
  categories: Category[] | null;
  catalogCourses: CatalogCourse[] | null;
  featuredCourses: FeaturedCourse[] | null;
  recommendationRules: RecommendationRule[] | null;
  loading: boolean;
  error: string | null;

  fetchCategories: () => Promise<void>;
  fetchCatalogCourses: () => Promise<void>;
  fetchFeaturedCourses: () => Promise<void>;
  fetchRecommendationRules: () => Promise<void>;

  // Category actions
  addCategory: (category: Category) => void;
  updateCategory: (id: string, updates: Partial<Category>) => void;
  toggleCategoryExpand: (id: string) => void;
  addSubcategory: (parentId: string, subcategory: Category) => void;

  // Featured course actions
  addFeaturedCourse: (course: FeaturedCourse) => void;
  toggleFeaturedCourseActive: (courseId: string) => void;
}

const useCatalogManagementStore = create<CatalogManagementState>((set, get) => ({
  categories: null,
  catalogCourses: null,
  featuredCourses: null,
  recommendationRules: null,
  loading: false,
  error: null,

  fetchCategories: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCategories();
      set({ categories: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCatalogCourses: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCatalogCourses();
      set({ catalogCourses: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchFeaturedCourses: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getFeaturedCourses();
      set({ featuredCourses: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRecommendationRules: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getRecommendationRules();
      set({ recommendationRules: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  addCategory: (category: Category) => {
    const { categories } = get();
    if (categories) {
      set({ categories: [...categories, category] });
    }
  },

  updateCategory: (id: string, updates: Partial<Category>) => {
    const { categories } = get();
    if (categories) {
      set({
        categories: categories.map(cat =>
          cat.id === id ? { ...cat, ...updates } : cat
        ),
      });
    }
  },

  toggleCategoryExpand: (id: string) => {
    const { categories } = get();
    if (categories) {
      set({
        categories: categories.map(cat =>
          cat.id === id ? { ...cat, isExpanded: !cat.isExpanded } : cat
        ),
      });
    }
  },

  addSubcategory: (parentId: string, subcategory: Category) => {
    const { categories } = get();
    if (categories) {
      set({
        categories: categories.map(cat => {
          if (cat.id === parentId) {
            return {
              ...cat,
              subcategories: [...(cat.subcategories || []), subcategory],
              isExpanded: true,
            };
          }
          return cat;
        }),
      });
    }
  },

  addFeaturedCourse: (course: FeaturedCourse) => {
    const { featuredCourses } = get();
    if (featuredCourses) {
      set({ featuredCourses: [...featuredCourses, course] });
    }
  },

  toggleFeaturedCourseActive: (courseId: string) => {
    const { featuredCourses } = get();
    if (featuredCourses) {
      set({
        featuredCourses: featuredCourses.map(course =>
          course.id === courseId ? { ...course, isActive: !course.isActive } : course
        ),
      });
    }
  },
}));

export default useCatalogManagementStore;
